import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthenticationService, UserRolesProviderService } from '@damofire/data-security';
import { firstValueFrom, from, map, shareReplay } from 'rxjs';
import { Survey } from '../models/survey.model';

@Injectable({
  providedIn: 'root',
})
export class SurveyPermissionsService {
  private readonly userRolesProvider = inject(UserRolesProviderService);
  private readonly authService = inject(AuthenticationService, { optional: true });

  private readonly roles$ = from(this.userRolesProvider.roles()).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  readonly userRoles = toSignal(this.roles$, { initialValue: [] });

  readonly isSurveyAdmin = toSignal(this.roles$.pipe(map((roles) => roles.includes('GSR_NEXUS_SURVEY_ADMIN'))), { initialValue: false });

  readonly isDrawerOnly = toSignal(this.roles$.pipe(map((roles) => this.hasOnlyDrawerRole(roles))), { initialValue: false });

  readonly isReadOnly = computed(() => this.isDrawerOnly());

  readonly canEdit = toSignal(this.roles$.pipe(map((roles) => this.hasEditRole(roles))), { initialValue: false });

  canDelete(survey?: Pick<Survey, 'hubspotTicketId' | 'status'>): boolean {
    const roles = this.userRoles();
    if (roles.includes('GSR_NEXUS_SURVEY_ADMIN')) {
      return true;
    }
    if (roles.includes('GSR_NEXUS_SURVEYOR')) {
      if (!survey) {
        return false;
      }
      return !this.isPublishedToHubspot(survey);
    }
    return false;
  }

  private isPublishedToHubspot(survey: Pick<Survey, 'hubspotTicketId' | 'status'>): boolean {
    return survey.status === 'publishedToHubSpot' || !!survey.hubspotTicketId;
  }

  readonly canPublish = toSignal(this.roles$.pipe(map((roles) => this.hasEditRole(roles))), { initialValue: false });

  readonly canCreate = toSignal(this.roles$.pipe(map((roles) => this.hasEditRole(roles))), { initialValue: false });

  async canRead(): Promise<boolean> {
    const roles = await firstValueFrom(this.roles$);
    return this.hasAnyRole(roles, ['GSR_NEXUS_SURVEYOR', 'GSR_NEXUS_SURVEY_ADMIN', 'GSR_PRODUCTION_DRAWER']);
  }

  async isSurveyor(): Promise<boolean> {
    const roles = await firstValueFrom(this.roles$);
    return this.hasEditRole(roles) && !roles.includes('GSR_NEXUS_SURVEY_ADMIN');
  }

  async currentUserId(): Promise<string> {
    if (!this.authService) {
      throw new Error('AuthenticationService is required to call currentUserId()');
    }
    const user = await this.authService.currentUser();
    return user._id;
  }

  private hasOnlyDrawerRole(roles: string[]): boolean {
    return roles.includes('GSR_PRODUCTION_DRAWER') && !roles.includes('GSR_NEXUS_SURVEYOR') && !roles.includes('GSR_NEXUS_SURVEY_ADMIN');
  }

  private hasEditRole(roles: string[]): boolean {
    return roles.includes('GSR_NEXUS_SURVEYOR') || roles.includes('GSR_NEXUS_SURVEY_ADMIN');
  }

  private hasAnyRole(roles: string[], requiredRoles: string[]): boolean {
    return requiredRoles.some((role) => roles.includes(role));
  }
}
